import { PageKindSchema, SlugSchema } from "@aurumiq/cms-schema";
import { Button } from "@aurumiq/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@aurumiq/ui/components/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SelectField, TextField } from "@/components/field";
import type { CmsPageDetail, CmsPageKind } from "@/lib/types";
import { useCreatePage } from "./queries";

const schema = z.object({
  kind: PageKindSchema,
  title: z.string().trim().min(1, "Required").max(160),
  slug: SlugSchema,
});
type Values = z.infer<typeof schema>;

export function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function NewPageDialog({
  open,
  onOpenChange,
  kinds,
  defaultKind,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kinds: readonly CmsPageKind[];
  defaultKind: CmsPageKind;
  onCreated?: (page: CmsPageDetail) => void;
}) {
  const create = useCreatePage();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    values: { kind: defaultKind, title: "", slug: "" },
  });
  const err = (k: keyof Values) => form.formState.errors[k]?.message;
  const onSubmit = form.handleSubmit(async (v) => {
    const p = await create.mutateAsync(v);
    form.reset();
    onOpenChange(false);
    onCreated?.(p);
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>New page</DialogTitle>
            <DialogDescription>
              Starts as an empty draft; nothing is public until you publish.
            </DialogDescription>
          </DialogHeader>
          <SelectField label="Kind" error={err("kind")} {...form.register("kind")}>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </SelectField>
          <TextField
            label="Title"
            autoFocus
            error={err("title")}
            {...form.register("title", {
              onChange: (e) => {
                if (!form.formState.dirtyFields.slug)
                  form.setValue("slug", slugify(e.target.value));
              },
            })}
          />
          <TextField
            label="Slug"
            className="font-mono"
            error={err("slug")}
            hint="lowercase letters, digits and hyphens"
            {...form.register("slug")}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
